import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { AccountAuthService } from '../../shared/account-auth.service';

@Component({
  selector: 'app-forgot-password',
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './forgot-password.html',
  styleUrl: './forgot-password.scss',
})
export class ForgotPassword {
  private readonly fb = inject(FormBuilder);
  private readonly auth = inject(AccountAuthService);

  readonly requestForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
  });

  readonly resetForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    passcode: [
      '',
      [Validators.required, Validators.pattern(/^[0-9]{6}$/)],
    ],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required, Validators.minLength(8)]],
  });

  isRequesting = false;
  isResetting = false;
  infoMessage = '';
  errorMessage = '';

  requestReset() {
    if (this.requestForm.invalid || this.isRequesting) {
      this.requestForm.markAllAsTouched();
      return;
    }

    this.isRequesting = true;
    this.infoMessage = '';
    this.errorMessage = '';
    const { email } = this.requestForm.getRawValue();

    this.auth.forgotPassword(email).subscribe({
      next: (response) => {
        this.isRequesting = false;
        this.infoMessage =
          response.message ||
          'If an account exists for this email, a reset passcode has been sent.';
        this.resetForm.controls.email.setValue(email);
      },
      error: (message: string) => {
        this.isRequesting = false;
        this.errorMessage = message;
      },
    });
  }

  resetPassword() {
    if (this.resetForm.invalid || this.isResetting) {
      this.resetForm.markAllAsTouched();
      return;
    }

    this.isResetting = true;
    this.infoMessage = '';
    this.errorMessage = '';
    const { email, passcode, password, confirmPassword } =
      this.resetForm.getRawValue();
    if (password !== confirmPassword) {
      this.isResetting = false;
      this.resetForm.controls.confirmPassword.setErrors({ mismatch: true });
      this.resetForm.controls.confirmPassword.markAsTouched();
      this.errorMessage = 'Passwords do not match.';
      return;
    }

    this.auth.resetPassword(email, passcode, password).subscribe({
      next: (response) => {
        this.isResetting = false;
        this.infoMessage = response.message || 'Password reset successful.';
        this.resetForm.controls.passcode.reset('');
        this.resetForm.controls.password.reset('');
        this.resetForm.controls.confirmPassword.reset('');
      },
      error: (message: string) => {
        this.isResetting = false;
        this.errorMessage = message;
      },
    });
  }
}
